import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router'
import { ChevronDown, ChevronUp, Film, Image as ImageIcon, X } from 'lucide-react'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { mediaLibraryApi } from '../services/api'
import {
  ActivityList,
  ActivityRow,
  Alert,
  Badge,
  BoundedSplitPane,
  Button,
  CatalogGrid,
  ConfirmDialog,
  DataView,
  ErrorSummary,
  Field,
  FormLayout,
  FormLayoutActions,
  FormLayoutSection,
  IconButton,
  Input,
  LinkButton,
  PageHeader,
  PageLayout,
  StatePanel,
  Switch,
  Textarea,
} from '../components/common'
import styles from './MediaTemplatesPage.module.css'

const TEMPLATE_KIND = 'poster'
const PUBLISHABLE_STATUSES = new Set(['ready', 'attached', 'in_feed'])

function emptyForm() {
  return {
    name: '',
    description: '',
    asset_ids: [],
    is_active: true,
  }
}

function templateForm(template) {
  return {
    name: template?.name || '',
    description: template?.description || '',
    asset_ids: template?.asset_ids || [],
    is_active: template?.is_active ?? true,
  }
}

function assetTitle(asset) {
  return asset?.title || asset?.metadata?.original_filename || asset?.metadata?.filename || 'Untitled asset'
}

function assetURL(asset) {
  return asset?.url || asset?.external_url || ''
}

function mutationMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback
}

function TemplateAsset({ asset, selected }) {
  const url = assetURL(asset)
  return (
    <span className={styles.assetOption}>
      <span className={styles.assetPreview} aria-hidden="true">
        {asset.asset_type === 'image' && url ? <img src={url} alt="" loading="lazy" /> : null}
        {asset.asset_type === 'video' ? <Film size={24} /> : null}
        {asset.asset_type !== 'image' && asset.asset_type !== 'video' ? <ImageIcon size={24} /> : null}
      </span>
      <span className={styles.assetCopy}>
        <strong>{assetTitle(asset)}</strong>
        <span>{asset.asset_type || 'Media'}</span>
      </span>
      <Badge compact tone={selected ? 'success' : 'neutral'}>{selected ? 'Selected' : 'Select'}</Badge>
    </span>
  )
}

export default function MediaTemplatesPage() {
  const { currentOrg } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedTemplateID = searchParams.get('template')
  const [form, setForm] = useState(emptyForm)
  const [submitted, setSubmitted] = useState(false)
  const [pendingDelete, setPendingDelete] = useState(null)

  const templatesQuery = useQuery({
    queryKey: ['media-templates', slug, TEMPLATE_KIND],
    queryFn: () => mediaLibraryApi.listTemplates(slug, { template_kind: TEMPLATE_KIND }),
    enabled: Boolean(slug),
  })
  const assetsQuery = useInfiniteQuery({
    queryKey: ['media-template-assets', slug],
    queryFn: ({ pageParam = null }) => mediaLibraryApi.listAssets(slug, {
      source: 'library',
      include_associations: false,
      ...(pageParam ? { cursor: pageParam } : {}),
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage?.next_cursor ?? undefined,
    enabled: Boolean(slug),
  })

  const templates = useMemo(() => templatesQuery.data?.items || [], [templatesQuery.data?.items])
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateID) || null
  const allLoadedAssets = useMemo(
    () => assetsQuery.data?.pages?.flatMap((page) => page?.items || []) || [],
    [assetsQuery.data?.pages],
  )
  const loadedAssetsByID = useMemo(
    () => new Map(allLoadedAssets.map((asset) => [asset.id, asset])),
    [allLoadedAssets],
  )
  const publishableAssets = useMemo(
    () => allLoadedAssets.filter((asset) => (
      asset.source_kind !== 'post_media'
      && PUBLISHABLE_STATUSES.has(asset.status)
      && Boolean(assetURL(asset))
    )),
    [allLoadedAssets],
  )
  const publishableAssetsByID = useMemo(
    () => new Map(publishableAssets.map((asset) => [asset.id, asset])),
    [publishableAssets],
  )

  const isCatalogExhausted = Boolean(
    assetsQuery.isSuccess
    && !assetsQuery.hasNextPage
    && !assetsQuery.isFetchingNextPage,
  )

  const unavailableAssetIDs = useMemo(() => {
    return form.asset_ids.filter((id) => {
      const loaded = loadedAssetsByID.get(id)
      if (loaded) {
        return !publishableAssetsByID.has(id)
      }
      return isCatalogExhausted
    })
  }, [form.asset_ids, loadedAssetsByID, publishableAssetsByID, isCatalogExhausted])

  useEffect(() => {
    setForm(selectedTemplate ? templateForm(selectedTemplate) : emptyForm())
    setSubmitted(false)
  }, [selectedTemplate])

  const errors = useMemo(() => {
    const next = []
    if (!form.name.trim()) next.push('Name is required.')
    if (form.is_active && !form.asset_ids.length) next.push('Available templates need at least one media asset.')
    if (unavailableAssetIDs.length) next.push('Remove unavailable media before saving.')
    return next
  }, [form, unavailableAssetIDs.length])

  const invalidateTemplates = () => queryClient.invalidateQueries({ queryKey: ['media-templates', slug] })
  const createMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.createTemplate(slug, payload),
    onSuccess: (created) => {
      toast.success('Template created')
      invalidateTemplates()
      setSearchParams({ template: created.id })
    },
    onError: (error) => toast.error(mutationMessage(error, 'Failed to create template')),
  })
  const updateMutation = useMutation({
    mutationFn: (payload) => mediaLibraryApi.updateTemplate(slug, selectedTemplateID, payload),
    onSuccess: () => {
      toast.success('Template updated')
      invalidateTemplates()
    },
    onError: (error) => toast.error(mutationMessage(error, 'Failed to update template')),
  })
  const deleteMutation = useMutation({
    mutationFn: (id) => mediaLibraryApi.deleteTemplate(slug, id),
    onSuccess: () => {
      toast.success('Template deleted')
      setPendingDelete(null)
      setSearchParams({})
      invalidateTemplates()
    },
    onError: (error) => toast.error(mutationMessage(error, 'Failed to delete template')),
  })

  const save = (event) => {
    event.preventDefault()
    setSubmitted(true)
    if (errors.length) return
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      template_kind: TEMPLATE_KIND,
      asset_ids: form.asset_ids,
      defaults: {},
      is_active: form.is_active,
    }
    if (selectedTemplate) updateMutation.mutate(payload)
    else createMutation.mutate(payload)
  }

  const updateAssetSelection = (selectedKeys) => {
    setForm((current) => {
      const selected = new Set(selectedKeys)
      const retained = current.asset_ids.filter((id) => selected.has(id))
      const appended = selectedKeys.filter((id) => !retained.includes(id))
      return { ...current, asset_ids: [...retained, ...appended] }
    })
  }

  const moveAsset = (index, offset) => {
    setForm((current) => {
      const next = [...current.asset_ids]
      const destination = index + offset
      if (destination < 0 || destination >= next.length) return current
      ;[next[index], next[destination]] = [next[destination], next[index]]
      return { ...current, asset_ids: next }
    })
  }

  const removeAsset = (assetID) => {
    setForm((current) => ({ ...current, asset_ids: current.asset_ids.filter((id) => id !== assetID) }))
  }

  if (!slug) {
    return (
      <PageLayout size="wide">
        <PageHeader title="Post templates" context="Posts" />
        <StatePanel state="restricted" title="Select an organization" description="Choose an organization before managing post templates." />
      </PageLayout>
    )
  }

  const submitting = createMutation.isPending || updateMutation.isPending
  const selectedScheduleCount = Number(selectedTemplate?.schedule_count || 0)

  const editor = (
    <FormLayout
      onSubmit={save}
      errorSummary={submitted && errors.length ? (
        <ErrorSummary>
          <ul>{errors.map((error) => <li key={error}>{error}</li>)}</ul>
        </ErrorSummary>
      ) : null}
    >
      <FormLayoutSection
        title={selectedTemplate ? 'Edit template' : 'New template'}
        description="Templates group reusable media for scheduled posts. Post copy and timing stay on each schedule."
      >
        <Field label="Template name" required error={submitted && !form.name.trim() ? 'Enter a template name.' : null}>
          <Input value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
        </Field>
        <Field label="Description" optional>
          <Textarea value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} rows={3} />
        </Field>
      </FormLayoutSection>

      <FormLayoutSection title="Media" description="Select saved media, then set the publishing order.">
        {assetsQuery.isLoading && !publishableAssets.length ? <StatePanel state="loading" title="Loading media" compact /> : null}
        {assetsQuery.error && !publishableAssets.length ? (
          <StatePanel state="error" title="Media could not load" description={assetsQuery.error.message} onRetry={assetsQuery.refetch} compact />
        ) : null}
        {!assetsQuery.isLoading && !assetsQuery.error && !publishableAssets.length ? (
          <StatePanel
            state="empty"
            title="No publishable media"
            description="Add ready media before building an available template."
            action={<LinkButton href="/dashboard/feed/assets">Open media</LinkButton>}
            compact
          />
        ) : null}
        {publishableAssets.length ? (
          <CatalogGrid
            items={publishableAssets}
            selectedKeys={form.asset_ids}
            onSelectionChange={updateAssetSelection}
            renderItem={(asset, state) => <TemplateAsset asset={asset} selected={state.selected} />}
            multiple
            columns={3}
            density="dense"
            label="Library media"
          />
        ) : null}
        {assetsQuery.hasNextPage || assetsQuery.isFetchNextPageError || (assetsQuery.error && publishableAssets.length) ? (
          <div className={styles.loadMoreContainer}>
            {assetsQuery.isFetchNextPageError || assetsQuery.error ? (
              <Button
                type="button"
                variant="secondary"
                size="compact"
                onClick={() => assetsQuery.fetchNextPage()}
              >
                Retry loading more
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="compact"
                loading={assetsQuery.isFetchingNextPage}
                disabled={assetsQuery.isFetchingNextPage}
                onClick={() => assetsQuery.fetchNextPage()}
              >
                Load more
              </Button>
            )}
          </div>
        ) : null}

        {form.asset_ids.length ? (
          <ActivityList label="Template publishing order">
            {form.asset_ids.map((assetID, index) => {
              const publishableAsset = publishableAssetsByID.get(assetID)
              const loadedAsset = loadedAssetsByID.get(assetID)
              const isUnavailable = loadedAsset
                ? !publishableAsset
                : isCatalogExhausted

              const title = publishableAsset
                ? assetTitle(publishableAsset)
                : isUnavailable
                  ? (loadedAsset ? assetTitle(loadedAsset) : 'Unavailable media')
                  : 'Selected media'

              const description = publishableAsset
                ? publishableAsset.asset_type
                : isUnavailable
                  ? 'Remove this item before saving.'
                  : 'Selected from library'

              const badges = isUnavailable ? <Badge tone="danger">Unavailable</Badge> : null

              return (
                <ActivityRow
                  as="li"
                  key={assetID}
                  ordinal={index + 1}
                  title={title}
                  description={description}
                  badges={badges}
                  actions={(
                    <>
                      <IconButton label={`Move ${title} up`} variant="ghost" disabled={index === 0} onClick={() => moveAsset(index, -1)}><ChevronUp size={16} /></IconButton>
                      <IconButton label={`Move ${title} down`} variant="ghost" disabled={index === form.asset_ids.length - 1} onClick={() => moveAsset(index, 1)}><ChevronDown size={16} /></IconButton>
                      <IconButton label={`Remove ${title}`} variant="ghost" onClick={() => removeAsset(assetID)}><X size={16} /></IconButton>
                    </>
                  )}
                />
              )
            })}
          </ActivityList>
        ) : null}
        {submitted && form.is_active && !form.asset_ids.length ? <Alert variant="error">Select at least one media asset or save the template as a draft.</Alert> : null}
      </FormLayoutSection>

      <FormLayoutSection title="Availability">
        <Switch
          checked={form.is_active}
          onChange={(checked) => setForm((current) => ({ ...current, is_active: checked }))}
          label="Available for scheduling"
          description="Draft templates remain editable but cannot be selected on a schedule."
        />
        {selectedScheduleCount > 0 ? (
          <Alert variant="info">Used by {selectedScheduleCount} {selectedScheduleCount === 1 ? 'schedule' : 'schedules'}. Remove those schedules before deleting this template.</Alert>
        ) : null}
      </FormLayoutSection>

      <FormLayoutActions>
        {selectedTemplate ? <Button variant="secondary" onClick={() => setSearchParams({})}>New template</Button> : null}
        {selectedTemplate ? <Button variant="danger" disabled={selectedScheduleCount > 0} onClick={() => setPendingDelete(selectedTemplate)}>Delete template</Button> : null}
        <Button type="submit" loading={submitting}>{selectedTemplate ? 'Save template' : 'Create template'}</Button>
      </FormLayoutActions>
    </FormLayout>
  )

  const catalog = (
    <FormLayoutSection title="Templates" description={`${templates.length} ${templates.length === 1 ? 'template' : 'templates'}`}>
      <DataView
        loading={templatesQuery.isLoading}
        error={templatesQuery.error}
        items={templates}
        emptyTitle="No post templates"
        emptyDescription="Create a reusable media template for scheduled posts."
        onRetry={templatesQuery.refetch}
      >
        <ActivityList label="Post templates">
          {templates.map((template) => {
            const available = Boolean(template.is_active && template.is_schedulable)
            const status = available ? 'Available' : template.is_active ? 'Needs attention' : 'Draft'
            return (
              <ActivityRow
                as="li"
                compact
                key={template.id}
                title={template.name}
                meta={[
                  { label: 'Media', value: String(template.asset_ids?.length || 0) },
                  { label: 'Schedules', value: String(template.schedule_count || 0) },
                ]}
                badges={<Badge tone={available ? 'success' : template.is_active ? 'warning' : 'neutral'}>{status}</Badge>}
                onSelect={() => setSearchParams({ template: template.id })}
                selectLabel={`Edit ${template.name}`}
              />
            )
          })}
        </ActivityList>
      </DataView>
    </FormLayoutSection>
  )

  return (
    <PageLayout size="wide">
      <PageHeader
        title="Post templates"
        context="Posts"
        subtitle="Build reusable media sets for scheduled posts."
        actions={<LinkButton href="/dashboard/feed/posts/scheduling" variant="secondary">Scheduling</LinkButton>}
      />
      <BoundedSplitPane primary={editor} inspector={catalog} inspectorLabel="Post templates" stackInspectorOnMobile />
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete template?"
        message={pendingDelete ? `Delete “${pendingDelete.name}”? This cannot be undone.` : ''}
        confirmLabel="Delete template"
        isDestructive
        loading={deleteMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => deleteMutation.mutate(pendingDelete.id)}
      />
    </PageLayout>
  )
}
